import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Button,
  Snackbar,
  Typography,
  Box
} from "@mui/material";
import dayjs from "dayjs";

interface TimeWindow {
  start: string;
  end: string;
  work_time?: number;
}

interface WeekdaySchedule {
  [weekday: string]: TimeWindow[];
}

interface DetailedPlan {
  [date: string]: TimeWindow[];
}

interface WorkerScheduleEditorProps {
  workerId: string | number;
  planId: string | number;
  month: string; // e.g. "2024-07"
  initialSchedule: WeekdaySchedule | { detailed_plan: DetailedPlan };
  onSave?: (schedule: any) => void;
}

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function getDaysInMonth(month: string): string[] {
  const [year, m] = month.split("-").map(Number);
  const days: string[] = [];
  const date = dayjs(`${year}-${m}-01`);
  const daysInMonth = date.daysInMonth();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(date.date(d).format("YYYY-MM-DD"));
  }
  return days;
}

function getTotalHours(windows: TimeWindow[]): number {
  let total = 0;
  for (const w of windows) {
    if (w.start && w.end) {
      const start = dayjs(w.start, "HH:mm");
      const end = dayjs(w.end, "HH:mm");
      total += end.diff(start, "minute") / 60;
    }
  }
  return total;
}

const colorForHours = (hours: number) => {
  if (hours === 0) return "#fff";
  if (hours < 4) return "#e0f7fa";
  if (hours < 8) return "#80cbc4";
  return "#26a69a";
};

const WorkerScheduleEditor: React.FC<WorkerScheduleEditorProps> = ({
  // workerId,
  // planId,
  month,
  initialSchedule,
  onSave,
}) => {
  // Mode: "weekday" or "per-day"
  const [mode, setMode] = useState(
    initialSchedule && "detailed_plan" in initialSchedule ? "per-day" : "weekday"
  );
  // State for both modes
  const [weekdaySchedule, setWeekdaySchedule] = useState<WeekdaySchedule>(
    mode === "weekday" ? (initialSchedule as WeekdaySchedule) : {
      monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: []
    }
  );
  const [detailedPlan, setDetailedPlan] = useState<DetailedPlan>(
    mode === "per-day" && "detailed_plan" in initialSchedule && typeof initialSchedule.detailed_plan === 'object' && !Array.isArray(initialSchedule.detailed_plan)
      ? initialSchedule.detailed_plan
      : (() => {
          const days = getDaysInMonth(month);
          const obj: DetailedPlan = {};
          days.forEach(d => { obj[d] = []; });
          return obj;
        })()
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // Track which work_time fields have been manually edited
  const [manualWorkTime, setManualWorkTime] = useState<{ [key: string]: { [idx: number]: boolean } }>({});

  // Helper to get work_time (auto-calc if not set or not manually edited)
  const getWorkTime = (tw: TimeWindow) => {
    if (typeof tw.work_time === 'number') return tw.work_time;
    if (tw.start && tw.end) {
      const start = dayjs(tw.start, "HH:mm");
      const end = dayjs(tw.end, "HH:mm");
      return end.diff(start, "minute") / 60;
    }
    return 0;
  };

  // When switching modes, copy data if possible
  const handleModeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      // Switch to per-day: expand weekday schedule to all days
      const days = getDaysInMonth(month);
      const newDetailed: DetailedPlan = {};
      days.forEach(date => {
        const weekday = dayjs(date).format("dddd").toLowerCase();
        newDetailed[date] = weekdaySchedule[weekday] ? [...weekdaySchedule[weekday]] : [];
      });
      setDetailedPlan(newDetailed);
      setMode("per-day");
    } else {
      // Switch to weekday: collapse per-day to weekday averages
      const newWeekday: WeekdaySchedule = { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] };
      Object.entries(detailedPlan).forEach(([date, windows]) => {
        const weekday = dayjs(date).format("dddd").toLowerCase();
        if (Array.isArray(windows) && windows.length > 0) newWeekday[weekday] = windows;
      });
      setWeekdaySchedule(newWeekday);
      setMode("weekday");
    }
  };

  // Handlers for editing
  const handleTimeChange = (dateOrWeekday: string, idx: number, field: "start" | "end", value: string) => {
    if (mode === "per-day") {
      setDetailedPlan(prev => {
        const arr = [...(prev[dateOrWeekday] || [])];
        arr[idx] = { ...arr[idx], [field]: value };
        // If work_time was not manually set, recalc
        if (!manualWorkTime[dateOrWeekday]?.[idx]) {
          const start = field === 'start' ? value : arr[idx].start;
          const end = field === 'end' ? value : arr[idx].end;
          if (start && end) {
            const st = dayjs(start, "HH:mm");
            const en = dayjs(end, "HH:mm");
            arr[idx].work_time = en.diff(st, "minute") / 60;
          }
        }
        return { ...prev, [dateOrWeekday]: arr };
      });
    } else {
      setWeekdaySchedule(prev => {
        const arr = [...(prev[dateOrWeekday] || [])];
        arr[idx] = { ...arr[idx], [field]: value };
        if (!manualWorkTime[dateOrWeekday]?.[idx]) {
          const start = field === 'start' ? value : arr[idx].start;
          const end = field === 'end' ? value : arr[idx].end;
          if (start && end) {
            const st = dayjs(start, "HH:mm");
            const en = dayjs(end, "HH:mm");
            arr[idx].work_time = en.diff(st, "minute") / 60;
          }
        }
        return { ...prev, [dateOrWeekday]: arr };
      });
    }
  };
  const handleWorkTimeChange = (dateOrWeekday: string, idx: number, value: number) => {
    if (mode === "per-day") {
      setDetailedPlan(prev => {
        const arr = [...(prev[dateOrWeekday] || [])];
        arr[idx] = { ...arr[idx], work_time: value };
        return { ...prev, [dateOrWeekday]: arr };
      });
    } else {
      setWeekdaySchedule(prev => {
        const arr = [...(prev[dateOrWeekday] || [])];
        arr[idx] = { ...arr[idx], work_time: value };
        return { ...prev, [dateOrWeekday]: arr };
      });
    }
    setManualWorkTime(prev => ({
      ...prev,
      [dateOrWeekday]: { ...(prev[dateOrWeekday] || {}), [idx]: true }
    }));
  };
  const handleAddTimeWindow = (dateOrWeekday: string) => {
    if (mode === "per-day") {
      setDetailedPlan(prev => ({ ...prev, [dateOrWeekday]: [...(prev[dateOrWeekday] || []), { start: "09:00", end: "18:00" }] }));
    } else {
      setWeekdaySchedule(prev => ({ ...prev, [dateOrWeekday]: [...(prev[dateOrWeekday] || []), { start: "09:00", end: "18:00" }] }));
    }
  };
  const handleRemoveTimeWindow = (dateOrWeekday: string, idx: number) => {
    if (mode === "per-day") {
      setDetailedPlan(prev => {
        const arr = [...(prev[dateOrWeekday] || [])];
        arr.splice(idx, 1);
        return { ...prev, [dateOrWeekday]: arr };
      });
    } else {
      setWeekdaySchedule(prev => {
        const arr = [...(prev[dateOrWeekday] || [])];
        arr.splice(idx, 1);
        return { ...prev, [dateOrWeekday]: arr };
      });
    }
  };

  // Save handler
  const handleSave = async () => {
    let payload: any;
    if (mode === "per-day") {
      payload = { detailed_plan: detailedPlan };
    } else {
      payload = weekdaySchedule;
    }
    if (onSave) onSave(payload);
    setSnackbarOpen(true);
  };

  // Render table rows
  const days = useMemo(() => getDaysInMonth(month), [month]);

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center">
        <Typography variant="subtitle1" mr={2}>Mode:</Typography>
        <Typography variant="body2" mr={1} color={mode === "weekday" ? "primary" : undefined} fontWeight={mode === "weekday" ? 600 : 400}>Weekly</Typography>
        <Switch
          checked={mode === "per-day"}
          onChange={handleModeSwitch}
          inputProps={{ 'aria-label': 'Schedule mode' }}
        />
        <Typography variant="body2" ml={1} color={mode === "per-day" ? "primary" : undefined} fontWeight={mode === "per-day" ? 600 : 400}>Daily</Typography>
      </Box>
      {mode === "per-day" ? (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Working Hours</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map(date => {
                const windows = detailedPlan[date] || [];
                const total = getTotalHours(windows);
                const isWeekend = [0, 6].includes(dayjs(date).day());
                return (
                  <TableRow key={date} sx={isWeekend ? { backgroundColor: '#FFF3E0' } : {}}>
                    <TableCell>
                      <Typography variant="body2">{date} ({dayjs(date).format("ddd")})</Typography>
                    </TableCell>
                    <TableCell sx={{ background: colorForHours(total), borderRadius: 1 }}>
                      {windows.map((w, idx) => (
                        <Box key={idx} display="inline-flex" alignItems="center" mr={1}>
                          <input
                            type="time"
                            value={w.start}
                            onChange={e => handleTimeChange(date, idx, "start", e.target.value)}
                            style={{ width: 80 }}
                          />
                          <Typography variant="body2" mx={0.5}>-</Typography>
                          <input
                            type="time"
                            value={w.end}
                            onChange={e => handleTimeChange(date, idx, "end", e.target.value)}
                            style={{ width: 80 }}
                          />
                          <Typography variant="body2" mx={0.5}>=</Typography>
                          <input
                            type="number"
                            min={0}
                            step={0.25}
                            value={getWorkTime(w)}
                            onChange={e => handleWorkTimeChange(date, idx, parseFloat(e.target.value))}
                            style={{ width: 60 }}
                          />
                          <Typography variant="body2" mx={0.5}>h</Typography>
                          <Button size="small" onClick={() => handleRemoveTimeWindow(date, idx)}>-</Button>
                        </Box>
                      ))}
                      <Button size="small" onClick={() => handleAddTimeWindow(date)}>+</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Weekday</TableCell>
                <TableCell>Working Hours</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {WEEKDAYS.map(wd => {
                const windows = weekdaySchedule[wd] || [];
                const total = getTotalHours(windows);
                return (
                  <TableRow key={wd}>
                    <TableCell>
                      <Typography variant="body2">{wd.charAt(0).toUpperCase() + wd.slice(1)}</Typography>
                    </TableCell>
                    <TableCell sx={{ background: colorForHours(total), borderRadius: 1 }}>
                      {windows.map((w, idx) => (
                        <Box key={idx} display="inline-flex" alignItems="center" mr={1}>
                          <input
                            type="time"
                            value={w.start}
                            onChange={e => handleTimeChange(wd, idx, "start", e.target.value)}
                            style={{ width: 80 }}
                          />
                          <Typography variant="body2" mx={0.5}>-</Typography>
                          <input
                            type="time"
                            value={w.end}
                            onChange={e => handleTimeChange(wd, idx, "end", e.target.value)}
                            style={{ width: 80 }}
                          />
                          <Typography variant="body2" mx={0.5}>=</Typography>
                          <input
                            type="number"
                            min={0}
                            step={0.25}
                            value={getWorkTime(w)}
                            onChange={e => handleWorkTimeChange(wd, idx, parseFloat(e.target.value))}
                            style={{ width: 60 }}
                          />
                          <Typography variant="body2" mx={0.5}>h</Typography>
                          <Button size="small" onClick={() => handleRemoveTimeWindow(wd, idx)}>-</Button>
                        </Box>
                      ))}
                      <Button size="small" onClick={() => handleAddTimeWindow(wd)}>+</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box mt={2}>
        <Button variant="contained" color="primary" onClick={handleSave} sx={{ mr: 2 }}>Save</Button>
        {/* Optionally add Cancel button */}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Schedule saved!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkerScheduleEditor; 